// Pure per-tick step of the queue DELIVERY alerts (#233). Extracted verbatim
// from the PositionTracker effect so the crossing rules are focused-testable —
// same discipline as stepFellBehind and stepDivergenceLatch.
//
// This module reports EVENTS ONLY. It applies no sound/visual preference, plays
// nothing, renders nothing, and touches no storage. It also never modifies a
// queue value: `queue` / `queueAbove` arrive already measured and leave
// untouched in the next maps.
//
// NOTE (#275): PositionTracker no longer calls this directly. It calls
// `stepConfirmedQueueDelivery` in ./confirmCrossing, which wraps this step and
// holds a crossing until a SECOND source reading agrees — Kalshi's bulk queue
// response oscillates for an unchanged resting order. The crossing rules below
// are unchanged and still decide WHAT a crossing is; confirmation only decides
// WHEN one is delivered.
//
// Rules (unchanged from the inline loop):
//  - Both queue cues fire on a DOWNWARD crossing: `last > threshold && current
//    <= threshold`. Advancing toward the front is the urgency direction.
//  - A tick that crosses BOTH boundaries records BOTH crossings. Each tier is
//    reported independently because each tier is now delivered independently:
//    with critical muted and warning enabled, collapsing the pair into one
//    critical event silently swallowed a cue the user had left switched on.
//    "One sound per cadence" and "critical outranks warning" are preserved
//    downstream instead — by `selectQueueSoundChannel` and by OrderCard's
//    `queueWarningFlash && !queueCriticalFlash` precedence — so with both
//    tiers enabled the observable result is unchanged.
//  - The pair is ORDER-CORRECTED before use: critical is the nearer-the-front
//    (smaller) bound. A stored pair that arrives inverted — only reachable
//    through hand-edited or foreign-written storage, never through the
//    clamping setter — would otherwise deliver the tiers backwards, firing
//    "critical" far from the fill and "warning" at the front. Same defence
//    QueueVelocityTracker.setThresholds already applies to its own pair, and
//    it reads thresholds only: no stored value is rewritten.
//  - First sight (no prior sample: new order, page load, reconnect) seeds
//    silently, even when already past a threshold.
//  - A null `queue` drops the sample, making the next numeric reading a silent
//    first sight.
//  - Orders absent from `metricsByOrderId` are pruned (both maps are rebuilt).
//
// The mirror-image direction — a better-priced level forming above, or the
// order slipping way behind — stays stepFellBehind's job, including its
// `queueAbove 0 -> positive` new-level cue.

import { stepFellBehind } from './fellBehind';
import type { AlertChannelId, AlertChannelSettings } from '@/lib/alerts/registry';

export interface QueueDeliveryThresholds {
  /** Contracts ahead at which the warning cue fires, crossing downward. */
  warning: number;
  /** Contracts ahead at which the critical cue fires, crossing downward. */
  critical: number;
  /** Contracts ahead at which the way-behind cue fires, crossing upward. */
  fellBehind: number;
}

export interface QueueDeliveryStepResult {
  nextQueue: Map<string, number>;
  nextAbove: Map<string, number>;
  warningOrderIds: string[];
  criticalOrderIds: string[];
  fellBehindOrderIds: string[];
}

interface QueueMetric {
  queue: number | null;
  queueAbove: number | null;
}

/**
 * Queue thresholds count DOWN toward the fill, so critical is the smaller of
 * the two. Reading them in order costs one comparison and makes an inverted
 * stored pair impossible to observe as inverted delivery.
 *
 * Exported so #275's confirmation layer confirms a candidate against the SAME
 * bound that crossed it. Two independent `Math.min` calls would drift the day
 * one of them is touched.
 */
export function orderedQueueBounds(
  thresholds: Pick<QueueDeliveryThresholds, 'warning' | 'critical'>
): { criticalAt: number; warningAt: number } {
  return {
    criticalAt: Math.min(thresholds.critical, thresholds.warning),
    warningAt: Math.max(thresholds.critical, thresholds.warning),
  };
}

export function stepQueueDelivery(
  prevQueue: ReadonlyMap<string, number>,
  prevAbove: ReadonlyMap<string, number>,
  metricsByOrderId: ReadonlyMap<string, QueueMetric>,
  thresholds: QueueDeliveryThresholds
): QueueDeliveryStepResult {
  const nextQueue = new Map<string, number>();
  const warningOrderIds: string[] = [];
  const criticalOrderIds: string[] = [];

  const { criticalAt, warningAt } = orderedQueueBounds(thresholds);

  for (const [orderId, metrics] of metricsByOrderId) {
    const q = metrics.queue;
    if (q == null) continue; // drop the sample; next numeric read reseeds silently
    nextQueue.set(orderId, q);
    const last = prevQueue.get(orderId);
    if (last == null) continue; // first sight: silent seed
    // Independent `if`s, not `else if`: a tick that dives through both bands
    // genuinely crossed both, and a muted critical must be able to fall
    // through to an enabled warning.
    if (last > criticalAt && q <= criticalAt) criticalOrderIds.push(orderId);
    if (last > warningAt && q <= warningAt) warningOrderIds.push(orderId);
  }

  const { nextAbove, firedOrderIds } = stepFellBehind(
    prevAbove,
    metricsByOrderId,
    thresholds.fellBehind
  );

  return {
    nextQueue,
    nextAbove,
    warningOrderIds,
    criticalOrderIds,
    fellBehindOrderIds: firedOrderIds,
  };
}

/** Highest-urgency first. Preserves the shipped queue cadence priority. */
const SOUND_PRIORITY = [
  { id: 'queueCritical', fired: 'criticalOrderIds' },
  { id: 'queueWarning', fired: 'warningOrderIds' },
  { id: 'fellBehind', fired: 'fellBehindOrderIds' },
] as const;

export type QueueSoundChannelId = (typeof SOUND_PRIORITY)[number]['id'];

/**
 * Which single queue tone to play this tick, or null for silence — one sound
 * per cadence, exactly as today.
 *
 * A fired channel whose sound is muted does NOT silence the tick: selection
 * falls THROUGH to the next channel that both fired and is audible. It never
 * falls through to a channel that did not fire.
 *
 * Reads `soundEnabled` only. Visual delivery is independent and is decided by
 * the caller, and the per-channel volume is applied by the caller too, so this
 * stays free of audio concerns.
 */
export function selectQueueSoundChannel(
  events: QueueDeliveryStepResult,
  channels: Readonly<Record<AlertChannelId, AlertChannelSettings>>
): QueueSoundChannelId | null {
  for (const { id, fired } of SOUND_PRIORITY) {
    if (events[fired].length > 0 && channels[id].soundEnabled) return id;
  }
  return null;
}
